import { FormEvent, KeyboardEvent, ReactNode, useEffect, useState } from 'react';
import {
  createTag,
  deleteContributor,
  deleteTag,
  findOrCreateContributor,
  listContributors,
  listTags,
  recolorTag,
  renameContributor,
  renameTag,
  TAG_PALETTE,
  tagTextColor,
} from '@chinese-reader/shared';
import type { Contributor, Tag } from '@chinese-reader/shared';
import Books from './Books';

// Tags and Contributors, the two pieces of Book metadata that exist independently of any Book.
// Both are small, flat, and edited rarely — a table with inline editing rather than the stacked
// cards and modals the mobile screens use, because a keyboard and a wide screen make the whole set
// visible at once (ADR 0036).
//
// Every call here comes from @chinese-reader/shared. That is not incidental: those functions carry
// the rules — name normalisation, clash detection, colour assignment — and a Console that reached
// for Supabase directly would be a second implementation of them (ADR 0036).

type Tab = 'books' | 'tags' | 'contributors';

function message(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

// Errors carry the row they belong to, so a refused rename appears beside the input that caused it
// rather than in a banner at the top of the panel — far enough away from the row being edited to be
// missed entirely, which is how a rejected rename first read as a successful one.
// `scope` is a row id, 'new' for the add form, or null for a failure to load the list at all.
interface ScopedError {
  scope: string | null;
  text: string;
}

// Three tabs now, not two: Books is the work, tags and contributors are reference data edited
// rarely. It defaults to Books for that reason. Issue 07 restructures this around a real Book page.
export default function Metadata() {
  const [tab, setTab] = useState<Tab>('books');

  return (
    <section className="metadata">
      <div className="metadata-tabs" role="tablist" aria-label="Console sections">
        <button
          className={tab === 'books' ? 'tab active' : 'tab'}
          onClick={() => setTab('books')}
          role="tab"
          aria-selected={tab === 'books'}
        >
          Books
        </button>
        <button
          className={tab === 'tags' ? 'tab active' : 'tab'}
          onClick={() => setTab('tags')}
          role="tab"
          aria-selected={tab === 'tags'}
        >
          Tags
        </button>
        <button
          className={tab === 'contributors' ? 'tab active' : 'tab'}
          onClick={() => setTab('contributors')}
          role="tab"
          aria-selected={tab === 'contributors'}
        >
          Contributors
        </button>
      </div>
      {tab === 'books' ? <Books /> : tab === 'tags' ? <TagsView /> : <ContributorsView />}
    </section>
  );
}

function TagsView() {
  const [tags, setTags] = useState<Tag[]>([]);
  const [name, setName] = useState('');
  const [error, setError] = useState<ScopedError | null>(null);
  const [loading, setLoading] = useState(true);
  // Which row is mid-write, so only that row's controls disable. A single boolean would freeze the
  // whole table while one tag renames.
  const [busy, setBusy] = useState<string | null>(null);

  // Loaded once. The list is updated in place after each write rather than refetched: these are
  // two admins editing a handful of rows, so a stale view would need them editing the same tag in
  // the same minute, and a reload settles it.
  useEffect(() => {
    listTags()
      .then(setTags)
      .catch((err) => setError({ scope: null, text: message(err) }))
      .finally(() => setLoading(false));
  }, []);

  const add = async (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim()) return;
    setBusy('new');
    setError(null);
    try {
      // createTag is find-or-create, so adding an existing tag returns it rather than failing —
      // hence the dedupe on id before appending.
      const tag = await createTag(name);
      setTags((current) =>
        current.some((item) => item.id === tag.id)
          ? current
          : [...current, tag].sort((a, b) => a.name.localeCompare(b.name)),
      );
      setName('');
    } catch (err) {
      setError({ scope: 'new', text: message(err) });
    } finally {
      setBusy(null);
    }
  };

  // Name and colour are separate writes because they are separate operations in the shared API.
  // Both are sent only when they changed, so recolouring does not trip renameTag's clash check.
  // Returns whether the write landed. A row keeps its edits in local state, so a refusal — a name
  // that clashes with an existing tag, most often — has to be pushed back to the row: otherwise the
  // input keeps displaying the rejected text and looks exactly like a successful rename. That is
  // what made a failed rename read as two identical tags.
  const save = async (tag: Tag, nextName: string, nextColor: string): Promise<boolean> => {
    if (!nextName.trim()) {
      setError({ scope: tag.id, text: 'Tag name cannot be empty.' });
      return false;
    }
    setBusy(tag.id);
    setError(null);
    try {
      // renameTag returns the stored row. Using it rather than the typed input keeps the table
      // showing what the database holds — it lowercases and trims, and that rule stays in one
      // place rather than being re-implemented here.
      const renamed = nextName.trim() !== tag.name ? await renameTag(tag.id, nextName) : tag;
      if (nextColor !== tag.color) await recolorTag(tag.id, nextColor);
      setTags((current) =>
        current
          .map((item) =>
            item.id === tag.id ? { ...item, name: renamed.name, color: nextColor } : item,
          )
          .sort((a, b) => a.name.localeCompare(b.name)),
      );
      return true;
    } catch (err) {
      setError({ scope: tag.id, text: message(err) });
      return false;
    } finally {
      setBusy(null);
    }
  };

  // book_tags cascades on tag delete, so this removes the tag from every Book that carries it —
  // which the confirmation says out loud, because nothing else would reveal it.
  const remove = async (tag: Tag) => {
    if (!window.confirm(`Delete "${tag.name}" from all books?`)) return;
    setBusy(tag.id);
    setError(null);
    try {
      await deleteTag(tag.id);
      setTags((current) => current.filter((item) => item.id !== tag.id));
    } catch (err) {
      setError({ scope: tag.id, text: message(err) });
    } finally {
      setBusy(null);
    }
  };

  return (
    <MetadataTable
      title="Tags"
      description="Shared labels used to organize Books in the Library."
      loading={loading}
      error={error && error.scope === null ? error.text : ''}
    >
      <form className="add-row" onSubmit={add}>
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="New tag name"
          aria-label="New tag name"
        />
        <button disabled={busy === 'new'}>{busy === 'new' ? 'Adding…' : 'Add tag'}</button>
        {error?.scope === 'new' && <RowError text={error.text} />}
      </form>
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Color</th>
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {tags.map((tag) => (
            <TagRow
              key={tag.id}
              tag={tag}
              busy={busy === tag.id}
              error={error?.scope === tag.id ? error.text : ''}
              onSave={save}
              onDelete={remove}
            />
          ))}
        </tbody>
      </table>
      {!loading && tags.length === 0 && <p className="empty">No tags yet.</p>}
    </MetadataTable>
  );
}

function TagRow({
  tag,
  busy,
  error,
  onSave,
  onDelete,
}: {
  tag: Tag;
  busy: boolean;
  error: string;
  onSave: (tag: Tag, name: string, color: string) => Promise<boolean>;
  onDelete: (tag: Tag) => void;
}) {
  const [name, setName] = useState(tag.name);
  const [color, setColor] = useState(tag.color);

  // Follow the stored row whenever it changes — after a rename the server may return a normalised
  // form (lowercased, trimmed) that differs from what was typed, and the table should show what
  // was actually saved.
  useEffect(() => {
    setName(tag.name);
    setColor(tag.color);
  }, [tag.name, tag.color]);

  // Revert to the stored values when the write is refused, so a rejected edit cannot sit on screen
  // looking like it took.
  const commit = async (nextName: string, nextColor: string) => {
    const saved = await onSave(tag, nextName, nextColor);
    if (!saved) {
      setName(tag.name);
      setColor(tag.color);
    }
  };

  // Enter commits, so a rename never needs the mouse. The Save button stays for discoverability.
  const commitOnEnter = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') void commit(name, color);
  };

  return (
    <tr>
      <td>
        <input
          className="inline-input"
          value={name}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={commitOnEnter}
          aria-label={`Rename ${tag.name}`}
          disabled={busy}
        />
        {error && <RowError text={error} />}
      </td>
      <td>
        <div className="color-picker">
          {TAG_PALETTE.map((item) => (
            <button
              key={item}
              type="button"
              className={color.toUpperCase() === item ? 'swatch selected' : 'swatch'}
              style={{ backgroundColor: item }}
              // A colour choice saves immediately: there is nothing to type, so waiting for a
              // separate Save press would only invite forgetting it.
              onClick={() => {
                setColor(item);
                void commit(name, item);
              }}
              aria-label={`Set color ${item}`}
              disabled={busy}
            />
          ))}
        </div>
        <span className="tag-preview" style={{ backgroundColor: color, color: tagTextColor(color) }}>
          {name || 'preview'}
        </span>
      </td>
      <td className="actions">
        <button type="button" onClick={() => void commit(name, color)} disabled={busy}>
          Save
        </button>
        <button
          type="button"
          className="danger-button"
          onClick={() => void onDelete(tag)}
          disabled={busy}
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

function ContributorsView() {
  const [contributors, setContributors] = useState<Contributor[]>([]);
  const [name, setName] = useState('');
  const [error, setError] = useState<ScopedError | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    listContributors()
      .then(setContributors)
      .catch((err) => setError({ scope: null, text: message(err) }))
      .finally(() => setLoading(false));
  }, []);

  const add = async (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim()) return;
    setBusy('new');
    setError(null);
    try {
      // Find-or-create, matching how the Book editor adds a credit: typing a name that already
      // exists must reuse that Contributor rather than minting a duplicate.
      const contributor = await findOrCreateContributor(name);
      setContributors((current) =>
        current.some((item) => item.id === contributor.id)
          ? current
          : [...current, contributor].sort((a, b) => a.name.localeCompare(b.name)),
      );
      setName('');
    } catch (err) {
      setError({ scope: 'new', text: message(err) });
    } finally {
      setBusy(null);
    }
  };

  // Contributor names keep their case — they are people's names, unlike Tags, which are lowercase
  // labels.
  // Returns whether the write landed, for the same reason the Tags view does — see the note there.
  const save = async (contributor: Contributor, nextName: string): Promise<boolean> => {
    const trimmed = nextName.trim();
    if (!trimmed) {
      setError({ scope: contributor.id, text: 'Contributor name cannot be empty.' });
      return false;
    }
    setBusy(contributor.id);
    setError(null);
    try {
      if (trimmed !== contributor.name) await renameContributor(contributor.id, trimmed);
      setContributors((current) =>
        current
          .map((item) => (item.id === contributor.id ? { ...item, name: trimmed } : item))
          .sort((a, b) => a.name.localeCompare(b.name)),
      );
      return true;
    } catch (err) {
      setError({ scope: contributor.id, text: message(err) });
      return false;
    } finally {
      setBusy(null);
    }
  };

  const remove = async (contributor: Contributor) => {
    if (!window.confirm(`Delete "${contributor.name}" from all books?`)) return;
    setBusy(contributor.id);
    setError(null);
    try {
      await deleteContributor(contributor.id);
      setContributors((current) => current.filter((item) => item.id !== contributor.id));
    } catch (err) {
      setError({ scope: contributor.id, text: message(err) });
    } finally {
      setBusy(null);
    }
  };

  return (
    <MetadataTable
      title="Contributors"
      description="People credited on Books as author, translator, or illustrator."
      loading={loading}
      error={error && error.scope === null ? error.text : ''}
    >
      <form className="add-row" onSubmit={add}>
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="New contributor name"
          aria-label="New contributor name"
        />
        <button disabled={busy === 'new'}>
          {busy === 'new' ? 'Adding…' : 'Add contributor'}
        </button>
        {error?.scope === 'new' && <RowError text={error.text} />}
      </form>
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {contributors.map((contributor) => (
            <ContributorRow
              key={contributor.id}
              contributor={contributor}
              busy={busy === contributor.id}
              error={error?.scope === contributor.id ? error.text : ''}
              onSave={save}
              onDelete={remove}
            />
          ))}
        </tbody>
      </table>
      {!loading && contributors.length === 0 && <p className="empty">No contributors yet.</p>}
    </MetadataTable>
  );
}

function ContributorRow({
  contributor,
  busy,
  error,
  onSave,
  onDelete,
}: {
  contributor: Contributor;
  busy: boolean;
  error: string;
  onSave: (contributor: Contributor, name: string) => Promise<boolean>;
  onDelete: (contributor: Contributor) => void;
}) {
  const [name, setName] = useState(contributor.name);

  useEffect(() => {
    setName(contributor.name);
  }, [contributor.name]);

  const commit = async (nextName: string) => {
    if (!(await onSave(contributor, nextName))) setName(contributor.name);
  };

  return (
    <tr>
      <td>
        <input
          className="inline-input"
          value={name}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') void commit(name);
          }}
          aria-label={`Rename ${contributor.name}`}
          disabled={busy}
        />
        {error && <RowError text={error} />}
      </td>
      <td className="actions">
        <button type="button" onClick={() => void commit(name)} disabled={busy}>
          Save
        </button>
        <button
          type="button"
          className="danger-button"
          onClick={() => void onDelete(contributor)}
          disabled={busy}
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

// Sits directly under the input that caused it. role="alert" so it is announced as well as seen —
// the row also reverts its value on refusal, and one of the two signals should land.
function RowError({ text }: { text: string }) {
  return (
    <p className="row-error" role="alert">
      {text}
    </p>
  );
}

function MetadataTable({
  title,
  description,
  loading,
  error,
  children,
}: {
  title: string;
  description: string;
  loading: boolean;
  error: string;
  children: ReactNode;
}) {
  return (
    <div className="metadata-panel">
      <div className="section-heading">
        <div>
          <h2>{title}</h2>
          <p className="muted">{description}</p>
        </div>
      </div>
      {/* role="alert" so a failed write is announced, not just tinted red. */}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {loading ? <p className="muted">Loading…</p> : children}
    </div>
  );
}
